import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import type { ThinkingItem } from '../../types/contentBlock';

export interface ThinkingBlockProps {
  /** 合并后的思考条目列表 */
  readonly items: readonly ThinkingItem[];
  /** 是否正在流式接收 */
  readonly isStreaming?: boolean;
}

/**
 * 思考过程渲染块（合并为单一块）
 *
 * - 渐变紫色边框
 * - 默认展开，可折叠
 * - max-height: 200px，内容滚动
 * - 流式时自动滚到底部
 */
export const ThinkingBlock: React.FC<ThinkingBlockProps> = memo(({
  items,
  isStreaming = false
}) => {
  // 非流式（已完成/历史消息）时默认折叠
  const [collapsed, setCollapsed] = useState(!isStreaming);
  const bodyRef = useRef<HTMLDivElement>(null);

  // 流式时自动滚到底部
  useEffect(() => {
    if (!collapsed && isStreaming && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [items, isStreaming, collapsed]);

  const handleToggle = useCallback((): void => {
    setCollapsed(prev => !prev);
  }, []);

  if (items.length === 0) return null;

  return (
    <div className={`thinking-block ${collapsed ? 'thinking-block--collapsed' : 'thinking-block--expanded'}`}>
      {/* 标题栏 */}
      <button
        type="button"
        className="thinking-block__header"
        onClick={handleToggle}
        aria-expanded={!collapsed}
      >
        <span className="thinking-block__icon">
          {isStreaming ? (
            <i className="fa-solid fa-spinner fa-spin" />
          ) : (
            <i className="fa-solid fa-brain" />
          )}
        </span>
        <span className="thinking-block__label">
          {isStreaming ? '思考中...' : '思考过程'}
        </span>
        <span className="thinking-block__count">{items.length}</span>
        <span className={`thinking-block__chevron ${collapsed ? 'thinking-block__chevron--closed' : ''}`}>
          <i className="fa-solid fa-chevron-down" />
        </span>
      </button>

      {/* 内容区域 */}
      {!collapsed && (
        <div className="thinking-block__body" ref={bodyRef}>
          {items.map((item, idx) => (
            <div key={item.id} className="thinking-block__item">
              {/* 工具标签 */}
              {item.tool && (
                <span className="thinking-block__tool-badge">
                  <i className="fa-solid fa-screwdriver-wrench" />
                  {item.tool}
                </span>
              )}
              <div className="thinking-block__text">
                {item.content || (isStreaming && idx === items.length - 1 ? '正在思考...' : '')}
              </div>
              {idx < items.length - 1 && <div className="thinking-block__divider" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

ThinkingBlock.displayName = 'ThinkingBlock';